/**
 * apps/web :: lib :: analytics
 *
 * Consumer of `@roadbridge/analytics`. Initialises PostHog using
 * `VITE_POSTHOG_KEY` from the Vite env and self-gates via the shared consent
 * helper. Mirrors `apps/marketing/src/lib/analytics.ts` — one canonical
 * `track()` pass-through, one `bootAnalytics()` idempotent init.
 *
 * ## Consent model for the authenticated product surface
 *
 * `apps/marketing` renders an explicit cookie banner before PostHog starts
 * sending events. `apps/web` is the signed-in learner app and does NOT have
 * that UI today — this ticket (ROADS-307) deliberately ships with an
 * IMPLICIT-ON-SIGNIN consent model: as soon as the user authenticates, we
 * treat that as consent to product-usage analytics and call
 * `grantConsent()` + `optIn()`. A proper consent UI on apps/web (mirroring
 * marketing's banner) is tracked as a follow-up. Until that lands, the
 * tradeoff is documented in the PR body + the package README.
 *
 * Hard rules preserved from marketing:
 *   - Every emission goes through `track()` — no bypassing.
 *   - `track()` is a no-op until BOTH `initAnalytics()` ran AND consent is
 *     granted. Never guess — compile-time type-safety already enforces
 *     event names + params shapes, but consent has to hold at runtime.
 *   - No PII. The event catalog in `packages/analytics/src/events.ts`
 *     enforces this structurally; never widen the interfaces there.
 */
import posthog from "posthog-js";
import {
  grantConsent,
  hasConsent,
  initAnalytics,
  optIn,
  optOut,
  POSTHOG_EU_HOST,
  reset as posthogReset,
  revokeConsent,
  track as rawTrack,
  type EventName,
  type ParamsFor,
} from "@roadbridge/analytics";
import { __resetForTests as __resetUpstreamForTests } from "@roadbridge/analytics/client";

let booted = false;

/**
 * Idempotent PostHog bootstrap. Safe to call from `main.tsx` plus the
 * auth-context "just signed in" callback — the second invocation is a
 * no-op because the upstream `initAnalytics()` guards against double-init.
 *
 * Silent in dev when `VITE_POSTHOG_KEY` is missing — we don't want every
 * `pnpm dev` to spam a "missing PostHog key" console error.
 */
export function bootAnalytics(): void {
  if (booted) return;
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  if (!apiKey) {
    // Dev without PostHog wired — stay silent so the console is clean.
    booted = true;
    return;
  }
  initAnalytics(apiKey, {
    client: posthog as unknown as Parameters<typeof initAnalytics>[1]["client"],
    apiHost: POSTHOG_EU_HOST,
    extra: {
      // Disable request-batching so every track() call triggers an
      // immediate POST to /e/. This keeps the Playwright specs
      // deterministic: without it, posthog-js buffers for up to 10s
      // before flushing and the e2e expect.poll runs out the clock.
      // Production impact is minimal — events are already small
      // (< 1 KB each) and the authenticated surface has low emission
      // volume per user (< 50 events per session in the happy path).
      request_batching: false,
    },
  });
  if (hasConsent()) optIn();
  booted = true;
}

/**
 * Implicit-consent handler.
 *
 * Called from the auth integration once `/auth/me` resolves to an
 * authenticated user. Grants consent + flips PostHog into capturing mode.
 * Safe to call multiple times (localStorage + PostHog both idempotent).
 */
export function onSignInConsent(): void {
  bootAnalytics();
  grantConsent();
  optIn();
}

/**
 * Sign-out cleanup.
 *
 * Revokes consent (so events stop firing on the anonymous post-logout
 * page), opts PostHog out, and clears the identity so the next signed-in
 * session is treated as a fresh distinct_id. Matches the marketing
 * "declineConsent" flow for symmetry.
 */
export function onSignOut(): void {
  revokeConsent();
  optOut();
  posthogReset();
}

/**
 * Pass-through typed `track()`. `@roadbridge/analytics` already no-ops
 * without init + consent, so callers can fire freely at component level
 * without wrapping in a conditional.
 *
 * Dev-only side-channel: in non-production builds we ALSO mirror every
 * call into `window.__rb_captured` so the Playwright e2e can inspect
 * emissions without depending on PostHog's opaque network shape
 * (gzip + base64 + sometimes sendBeacon). This is strictly a test seam
 * and is stripped by Vite's `import.meta.env.MODE === 'production'`
 * dead-code elimination. See `apps/web/e2e/analytics.spec.ts`.
 */
export function track<N extends EventName>(
  name: N,
  params: ParamsFor<N>,
): void {
  rawTrack(name, params);
  if (import.meta.env.MODE !== "production") {
    const w = globalThis as unknown as {
      __rb_captured?: Array<{ event: string; properties: Record<string, unknown> }>;
    };
    if (!w.__rb_captured) w.__rb_captured = [];
    w.__rb_captured.push({
      event: name as string,
      properties: params as unknown as Record<string, unknown>,
    });
  }
}

export type { EventName, ParamsFor };

/**
 * Test-only: reset the module-local `booted` flag. The upstream package's
 * `__resetForTests` handles its own client/init state; this just resets
 * our boot sentinel so `bootAnalytics()` can be called fresh in a test.
 */
export function __resetAnalyticsForTests(): void {
  booted = false;
  __resetUpstreamForTests();
}
